"use client";

// ── The shape of an alert on a contest page (`EX-ANN01`, `EX-ANN02`) ────────
// One newest item, what kind it is, how many more there are, a way to the full
// list, and "Got it". The announcement and the clarification alerts fill it;
// neither fetches anything (the inbox hooks own the poll and the marker).

import type { ReactNode } from "react";
import Link from "next/link";
import { Megaphone } from "lucide-react";

import { Button } from "@/components/ui/button";

export function ContestNoticeAlert({
  testId,
  label,
  heading,
  context,
  body,
  more,
  readAllLabel,
  readAllHref,
  onReadAll,
  gotItLabel,
  onSeen,
}: {
  testId: string;
  label: string;
  heading?: string | null;
  /** A quieter line above the body, e.g. the question an answer replies to. */
  context?: ReactNode;
  body: string;
  more?: string | null;
  readAllLabel: string;
  /** The workspace links to the contest page's tab... */
  readAllHref?: string;
  /** ...and the contest page switches to it. */
  onReadAll?: () => void;
  gotItLabel: string;
  onSeen: () => void;
}) {
  const readAll = readAllHref ? (
    <Link href={readAllHref} className="text-xs font-semibold text-amber-950 underline underline-offset-2">
      {readAllLabel}
    </Link>
  ) : onReadAll ? (
    <Button variant="tertiary" size="mini" onClick={onReadAll} className="text-amber-950 underline underline-offset-2">
      {readAllLabel}
    </Button>
  ) : null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid={testId}
      className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950"
    >
      <Megaphone aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-wide text-amber-800">{label}</p>
        {heading ? <p className="mt-0.5 text-sm font-semibold">{heading}</p> : null}
        {context ? <div className="mt-0.5 line-clamp-2 text-xs text-amber-900/80">{context}</div> : null}
        <p className="mt-0.5 line-clamp-3 whitespace-pre-wrap text-sm leading-relaxed">{body}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {more ? <span className="text-xs font-semibold">{more}</span> : null}
          {readAll}
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onSeen} className="shrink-0">
        {gotItLabel}
      </Button>
    </div>
  );
}
